/** Seats in Customize mode (E3, spec 2026-09-18 §6): the label and the RBAC
 * role names that may work the seat (spec D6 — empty = open to everyone
 * holding `tasks:work`). Role names are data, not definition: the picker
 * suggests the deployment's roles but accepts any name, and readiness
 * `process.seats` reports the unknown ones. SA-1060: a closed card whose
 * line counts the seats and names the unbound ones. */
import { Input, TagInput } from '@/components/ui'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { ProcessEditorCard } from './ProcessEditorCard'
import { SEATS_CARD, type EditorCards } from './editorCards'
import { seatsSummary } from './editorSummaries'

export function ProcessSeatsEditor({
  base,
  overlay,
  roles,
  onRoles,
  onLabel,
  cards,
  edited = false,
}: {
  base: ProcessWorkflowDefinitionJson
  overlay: WorkflowOverlayJson
  /** Role names to suggest (`/api/admin/roles`); may be empty. */
  roles: string[]
  onRoles: (seatId: string, roles: string[]) => void
  onLabel: (seatId: string, lang: string, text: string) => void
  /** SA-1060: the card's open state. */
  cards: EditorCards
  /** SA-1060: any overlay entry on a seat (ruling Q3). */
  edited?: boolean
}) {
  const languages = base.languages?.length ? base.languages : ['en']
  return (
    <ProcessEditorCard
      cardId={SEATS_CARD}
      title="Seats"
      label="Seats"
      summary={seatsSummary(base, overlay)}
      edited={edited}
      open={cards.isOpen(SEATS_CARD)}
      onOpenChange={(open) => cards.setOpen(SEATS_CARD, open)}
    >
      <p className="text-xs text-muted-foreground">
        Who may work each seat's tasks. A seat with no roles is open to everyone who can work tasks.
      </p>
      {Object.entries(base.seats).map(([id, seat]) => {
        const name = seat.label?.en ?? id
        return (
          <div key={id} className="grid gap-3 border-t border-border/60 pt-3 text-xs sm:grid-cols-2">
            <div className="space-y-2">
              {languages.map((l) => {
                const label = l === languages[0] ? `Label for ${name}` : `Label for ${name} (${l})`
                return (
                  <label key={l} className="block space-y-1 font-medium text-foreground">
                    <span>{l === languages[0] ? 'Label' : `Label (${l})`}</span>
                    <Input
                      aria-label={label}
                      value={overlay.seats?.[id]?.label?.[l] ?? seat.label?.[l] ?? ''}
                      onChange={(e) => onLabel(id, l, e.target.value)}
                    />
                  </label>
                )
              })}
            </div>
            <div className="space-y-1">
              <span className="font-medium text-foreground">Roles</span>
              <TagInput
                ariaLabel={`Roles for ${name}`}
                value={overlay.seats?.[id]?.roles ?? seat.roles ?? []}
                onChange={(next) => onRoles(id, next)}
                suggestions={roles}
                normalize={(raw) => raw.trim()}
                placeholder="Add a role and press Enter"
              />
            </div>
          </div>
        )
      })}
    </ProcessEditorCard>
  )
}
